import { ConsentPreferenceValue, ConsentPurposePreferenceInput } from '@/api/gql/graphql';
import {
  AirgapPurposes,
  AirgapPurposeValue,
} from '@/components/app/consent_management/airgap.types';

// Transcend manages these itself. The backend excludes only Essential and Unknown from its own
// consent-method derivation, so AlwaysBlock has to be dropped here or it can never read as
// accepted-all.
const NON_CONFIGURABLE_PURPOSES = ['AlwaysBlock', 'Essential', 'Unknown'];

const toPreferenceValue = (value: AirgapPurposeValue): ConsentPreferenceValue | null => {
  if (value === true || value === 'on') return ConsentPreferenceValue.Allowed;
  if (value === false || value === 'off') return ConsentPreferenceValue.Denied;
  // The typeof guard keeps a non-string value a future SDK release could report from throwing.
  if (typeof value === 'string' && value.startsWith('Auto')) return ConsentPreferenceValue.Auto;

  return null;
};

// An unmappable value is dropped rather than guessed at: the backend reads an omitted purpose as
// "not available", while an unknown value would reject the whole snapshot.
export const toPreferenceInput = (purposes: AirgapPurposes): ConsentPurposePreferenceInput[] =>
  Object.entries(purposes)
    .filter(([purpose]) => !NON_CONFIGURABLE_PURPOSES.includes(purpose))
    .map(([purpose, value]) => ({ purpose, value: toPreferenceValue(value) }))
    .filter((entry): entry is ConsentPurposePreferenceInput => entry.value !== null);

// AUTO is omitted: Airgap accepts the literal but re-resolves it on the spot, overwriting the
// restored timestamp and emitting a second consent-change event.
export const toAirgapPurposes = (preferences: ConsentPurposePreferenceInput[]): AirgapPurposes =>
  preferences.reduce<AirgapPurposes>((purposes, { purpose, value }) => {
    if (value === ConsentPreferenceValue.Allowed) purposes[purpose] = true;
    if (value === ConsentPreferenceValue.Denied) purposes[purpose] = false;

    return purposes;
  }, {});

// Identifies one effective preference map the way the backend compares it: purposes and values
// only, order-independent.
export const preferenceSignature = (preferences: ConsentPurposePreferenceInput[]): string =>
  preferences
    .map(({ purpose, value }) => `${purpose}=${value}`)
    .sort()
    .join(',');
